import { useState, useEffect, useCallback, useRef } from 'react';
import { ensureConnection, pingConnection, testConnection } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import logger from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';

const PING_INTERVAL = 60 * 1000; // 1 minute
const INACTIVE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
const MAX_RECONNECT_ATTEMPTS = 3;

export function useConnection() {
  const [isConnected, setIsConnected] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { toast } = useToast();
  
  // Use refs for values that should persist across renders but don't trigger re-renders
  const lastActivityRef = useRef(Date.now());
  const wasDisconnectedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const lastReconnectTimeRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track activity function
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Add event listeners to track user activity
  useEffect(() => {
    // Track user activity
    window.addEventListener('click', trackActivity);
    window.addEventListener('keypress', trackActivity);
    window.addEventListener('scroll', trackActivity);
    window.addEventListener('mousemove', trackActivity);
    
    // Initialize last activity
    trackActivity();
    
    return () => {
      window.removeEventListener('click', trackActivity);
      window.removeEventListener('keypress', trackActivity);
      window.removeEventListener('scroll', trackActivity);
      window.removeEventListener('mousemove', trackActivity);
    };
  }, [trackActivity]);

  // Function to check connection and reconnect if needed
  const checkConnection = useCallback(async (force = false) => {
    const now = Date.now();
    const inactive = now - lastActivityRef.current > INACTIVE_THRESHOLD;
    
    // Avoid reconnect attempts in quick succession
    const timeSinceLastAttempt = now - lastReconnectTimeRef.current;
    if (!force && timeSinceLastAttempt < 10000) { // Don't auto-retry within 10 seconds
      return;
    }
    
    // If we've been inactive and there's user activity, or force check is requested
    if ((inactive && lastActivityRef.current === now) || force) {
      setIsReconnecting(true);
      lastReconnectTimeRef.current = now;
      
      try {
        // First try to test the connection
        const testResult = await testConnection();
        
        if (!testResult.connected) {
          // If test fails, try an explicit reconnection
          const connected = await ensureConnection();
          setIsConnected(connected);
          
          if (!connected) {
            reconnectAttemptsRef.current++;
            wasDisconnectedRef.current = true;
            
            // Only show toast if this was forced or we've reached the max attempts
            if (force || reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
              toast({
                title: "Connection Issue",
                description: "There was a problem connecting to the server. Please refresh the page or check your internet connection.",
                variant: "destructive",
              });
              
              // Reset attempts after showing toast
              if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
                reconnectAttemptsRef.current = 0;
              }
            }
          } else {
            // Connection restored
            reconnectAttemptsRef.current = 0;
            if (force && wasDisconnectedRef.current) {
              // Only show the reconnection toast if there was a previous disconnect
              wasDisconnectedRef.current = false;
              toast({
                title: "Connection Restored",
                description: "Your connection has been restored.",
              });
            }
          }
        } else {
          // Test succeeded, connection is good
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
          
          if (force && wasDisconnectedRef.current) {
            wasDisconnectedRef.current = false;
            toast({
              title: "Connection Restored",
              description: "Your connection has been restored.",
            });
          }
        }
      } catch (error) {
        logger.error('Error checking connection', { 
          module: 'Connection',
          data: error
        });
        setIsConnected(false);
        wasDisconnectedRef.current = true;
        reconnectAttemptsRef.current++;
      } finally {
        setIsReconnecting(false);
      }
    }
  }, [toast]);

  // Function to force reconnection
  const reconnect = useCallback(async () => {
    // Reset reconnect attempts when user manually triggers reconnect
    reconnectAttemptsRef.current = 0;
    return checkConnection(true);
  }, [checkConnection]);

  // Set up periodic ping to check connection
  useEffect(() => {
    const pingTimer = setInterval(async () => {
      const now = Date.now();
      
      // If the user has been inactive for more than the threshold
      if (now - lastActivityRef.current > INACTIVE_THRESHOLD) {
        logger.info('User inactive, suspending regular pings', {
          module: 'Connection'
        });
        return;
      }
      
      // Perform a lightweight ping to keep connection alive
      const pingResult = await pingConnection();
      
      if (isConnected && !pingResult.connected) {
        // Only update state if connection changed from connected to disconnected
        setIsConnected(false);
        wasDisconnectedRef.current = true;
        logger.warn('Ping detected connection issue, attempting to reconnect...', {
          module: 'Connection'
        });
        checkConnection(false); // Don't force to avoid excessive notifications
      } else if (!isConnected && pingResult.connected) {
        // Connection restored during ping
        setIsConnected(true);
        wasDisconnectedRef.current = false;
        reconnectAttemptsRef.current = 0;
      }
    }, PING_INTERVAL);
    
    return () => clearInterval(pingTimer);
  }, [checkConnection, isConnected]);

  // Check connection when the component using this hook changes route
  useEffect(() => {
    const handleRouteChange = () => {
      // Check connection silently on route change without forcing a reconnect
      // to avoid unnecessary toast messages
      const silentCheck = async () => {
        try {
          const connected = await pingConnection();
          setIsConnected(connected.connected);
          
          if (connected.connected && wasDisconnectedRef.current) {
            wasDisconnectedRef.current = false;
            reconnectAttemptsRef.current = 0;
          }
        } catch (error) {
          logger.error('Error silently checking connection', {
            module: 'Connection',
            data: error
          });
        }
      };
      
      silentCheck();
    };
    
    // Add event listener for navigation events
    window.addEventListener('popstate', handleRouteChange);
    
    // Also check connection when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logger.info('Tab became visible, checking connection state...', { module: 'Connection' });
        
        // Cancel any pending reconnection attempts first
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        // Perform connection check with a slight delay to allow other systems to restore
        setTimeout(() => {
          const silentCheckWithReconnect = async () => {
            try {
              // First check if we already have a valid session
              const { data } = await supabase.auth.getSession();
              const hasSession = !!data?.session;
              
              // Then check connection
              const connected = await pingConnection();
              
              if (!connected.connected) {
                logger.warn('Connection lost while tab was inactive, attempting to reconnect...', {
                  module: 'Connection',
                  data: { hasSession }
                });
                
                // If we have a session but connection is broken, try to refresh
                if (hasSession) {
                  try {
                    await supabase.auth.refreshSession();
                    // Check connection again after refresh
                    const refreshedConnection = await pingConnection();
                    if (refreshedConnection.connected) {
                      setIsConnected(true);
                      wasDisconnectedRef.current = false;
                      reconnectAttemptsRef.current = 0;
                      return;
                    }
                  } catch (refreshError) {
                    logger.error('Failed to refresh session', {
                      module: 'Connection',
                      data: refreshError
                    });
                  }
                }
                
                // If still not connected, attempt full reconnection
                checkConnection(false); // Attempt reconnect but don't force notification
              } else {
                setIsConnected(true);
                wasDisconnectedRef.current = false;
                reconnectAttemptsRef.current = 0;
              }
            } catch (error) {
              logger.error('Error checking connection on visibility change', {
                module: 'Connection',
                data: error
              });
              
              // If an error occurs during connection check, attempt reconnection
              checkConnection(false);
            }
          };
          
          silentCheckWithReconnect();
        }, 500);
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkConnection]);

  return {
    isConnected,
    isReconnecting,
    reconnect,
  };
} 